import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Translator from 'translator';
import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { Add as AddIcon, Refresh as RefreshIcon, RemoveRedEye as ViewIcon, Delete as DeleteIcon } from '@material-ui/icons';
import { FHETable } from 'data/components';
import { Toastr, tools } from 'data/common';
import renderEnvComponent from 'data/common/renderEnvComponent';
import { getUsersManagers, deleteUserManager } from '../../data/UsersManagers/actions';

class UserManager extends PureComponent {
    static propTypes = {
        initialized: PropTypes.bool.isRequired,
        usersManagers: PropTypes.object.isRequired,
        deleteUserManager: PropTypes.func.isRequired,
        getUsersManagers: PropTypes.func.isRequired,
        profile: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired
    };

    constructor(props) {
        super(props);
        const { initialized } = props;

        this.state = {
            loading: !initialized
        };
    }

    componentDidMount() {
        const { initialized } = this.props;
        if (!initialized) this.refreshUsersManagers();
    }

    refreshUsersManagers = () => {
        const { getUsersManagers } = this.props;
        this.setState({ loading: true });
        return getUsersManagers()
            .catch(err => {
                const message = Translator.translateWithFallback(`api.message.${err.message ? err.message : err}`, 'api.message.default');
                Toastr.error(message);
            })
            .finally(() => {
                this.setState({ loading: false });
            });
    };

    navigateToDetail = rowData => {
        const { history } = this.props;
        return history.push({ pathname: `user-manager/detail/${rowData.id}` });
    };
    navigateToAdd = () => {
        const { history } = this.props;
        return history.push({ pathname: 'user-manager/add' });
    };

    handleDeleteUserManager = (row, callbackRowActionLoading) => {
        callbackRowActionLoading(true);
        Toastr.confirm(
            Translator.translate('generic.userManager.actions.deleteConfirm', row.firstname, row.lastname),
            Translator.translate('general.logout.confirmationTitle'),
            {
                onYes: () => {
                    this.deleteManager(row, callbackRowActionLoading);
                },
                onNo: () => {
                    callbackRowActionLoading(false);
                }
            }
        );
    };

    deleteManager = (row, callbackRowActionLoading) => {
        const { deleteUserManager } = this.props;

        deleteUserManager(Number(row.id))
            .then(() => {
                const message = Translator.translate('generic.userManager.actions.deleteSuccess', row.firstname, row.lastname);
                Toastr.success(message);
            })
            .catch(() => {
                const message = Translator.translate('generic.userManager.actions.deleteError', row.firstname, row.lastname);
                Toastr.error(message);
            })
            .finally(() => callbackRowActionLoading(false));
    };

    renderActions = memoizeOne(profile => {
        const { history } = this.props;
        let actions = {
            row: [],
            toolbar: [
                {
                    icon: RefreshIcon,
                    tooltip: Translator.translate(`generic.userManager.table.globalAction.refresh.tooltip`),
                    onClick: this.refreshUsersManagers
                }
            ]
        };
        if (profile.get('admin') || profile.getIn(['user_rights', history.environment], []).includes('userManagerAdd')) {
            actions.toolbar.push({
                icon: AddIcon,
                tooltip: Translator.translate(`generic.userManager.table.globalAction.add.tooltip`),
                onClick: this.navigateToAdd
            });
        }
        if (profile.get('admin') || profile.getIn(['user_rights', history.environment], []).includes('userManagerDetails')) {
            actions.row.push({
                icon: ViewIcon,
                onClick: this.navigateToDetail,
                tooltip: Translator.translate(`general.showDetail`)
            });
        }
        if (profile.get('admin') || profile.getIn(['user_rights', history.environment], []).includes('userManagerDelete')) {
            actions.row.push({
                icon: DeleteIcon,
                onClick: (row, callbackRowActionLoading) => this.handleDeleteUserManager(row, callbackRowActionLoading),
                tooltip: Translator.translate('generic.userManager.table.globalAction.delete.tooltip')
            });
        }
        return actions;
    });

    renderData = memoizeOne(usersManagers => {
        let result = [];
        usersManagers.map((userManager, userManagerId) => {
            result.push({
                id: parseInt(userManagerId),
                title: userManager.get('title'),
                email: userManager.get('email'),
                lastname: userManager.get('lastname'),
                firstname: userManager.get('firstname'),
                role: Translator.translate(`generic.userManager.table.${userManager.get('role')}`),
                code: userManager.get('code'),
            });
        });
        return result;
    });

    renderColumns = memoizeOne(profile => {
        const { history } = this.props;

        return [
            {
                title: Translator.translate('general.table.column.email'),
                name: 'email',
                action: (profile.get('admin') || profile.getIn(['user_rights', history.environment], []).includes('userManagerDetails')) && {
                    onClick: this.navigateToDetail,
                    tooltip: Translator.translate(`general.showDetail`)
                }
            },
            {
                title: Translator.translate('general.table.column.lastname'),
                name: 'lastname'
            },
            {
                title: Translator.translate('general.table.column.firstname'),
                name: 'firstname'
            },
            {
                title: Translator.translate('general.table.column.brand'),
                name: 'title'
            },
            {
                title: Translator.translate('general.table.column.role'),
                name: 'role'
            },
            {
                title: Translator.translate('general.table.column.code'),
                name: 'code',
            }
        ];
    });

    render() {
        const { loading } = this.state;
        const { profile, usersManagers } = this.props;

        return (
            <FHETable
                idTable="userManagerList"
                columns={this.renderColumns(profile)}
                data={this.renderData(usersManagers)}
                actions={this.renderActions(profile)}
                title={Translator.translate(`generic.userManager.table.title`)}
                emptyDataSourceMessage={Translator.translate(`generic.userManager.table.body.emptyDataSourceMessage`)}
                isLoading={loading}
            />
        );
    }
}

function mapStateToProps(state, props) {
    const { history } = props;
    return {
        profile: state.data.user,
        usersManagers: state.environments[history.environment].data.UsersManagers.get('list'),
        initialized: state.environments[history.environment].data.UsersManagers.get('initialized')
    };
}

export default withRouter(
    renderEnvComponent(
        'scenes/UserManager/index',
        connect(
            mapStateToProps,
            (dispatch, ownProps) =>
                bindActionCreators(tools.mergePropsToAction(ownProps.history.environment, { getUsersManagers, deleteUserManager }), dispatch)
        )(UserManager)
    )
);



// WEBPACK FOOTER //
// ./environments/genericEnv/scenes/UserManager/index.js